import React, { FC, useEffect, useRef, useState } from 'react';

import { Button, Grid } from '@material-ui/core';

import { Link } from 'react-router-dom';

import { DateRange } from '@material-ui/pickers';

import { useStyles } from '../styles';

import { useApiError, useLoading } from '../../../../providers';

import { getHighlightData } from '../../IBDashboardService';

import { ChartData, DocumentIcon, LineChart } from '../../../../components';

import { checkDataExist, ClientFilterType, HighlightDataItem, HighlightTypes } from '../../IBDashboardModels';

interface Props {
  dateRange: DateRange<Date>;
  partnership: string;
  currency?: string;
  highlightTypes: HighlightTypes[];
  emptyMessage: string;
  legendFirstItem: string;
  legendSecondItem: string;
  firstButtonName?: string;
  firstButtonPath?: string;
  secondButtonName?: string;
  secondButtonPath?: string;
  clientFilterType: ClientFilterType;
  order: Record<string, number>;
  colors: string[];
  valueType?: 'amount' | 'count';
}

interface HighlightResponse {
  type?: string;
  tType?: string;
  highlights?: HighlightDataItem[] | null;
}

const parseOrderKey = (orderKey: string): Record<string, number> =>
  orderKey
    .split(',')
    .filter(Boolean)
    .reduce<Record<string, number>>((result, item) => {
      const separatorIndex = item.lastIndexOf(':');

      if (separatorIndex === -1) {
        return result;
      }

      const key = item.slice(0, separatorIndex);

      const value = Number(item.slice(separatorIndex + 1));

      if (!Number.isNaN(value)) {
        result[key] = value;
      }

      return result;
    }, {});

export const DoubleGraphPanel: FC<Props> = ({
  currency,
  dateRange,
  order,
  partnership,
  highlightTypes,
  legendFirstItem,
  legendSecondItem,
  emptyMessage,
  firstButtonName,
  firstButtonPath,
  secondButtonName,
  secondButtonPath,
  clientFilterType,
  colors,
  valueType = 'amount',
}) => {
  const classes = useStyles();

  const { addError } = useApiError();
  const { setIsLoading } = useLoading();

  const requestIdRef = useRef(0);
  const addErrorRef = useRef(addError);
  const setIsLoadingRef = useRef(setIsLoading);

  const [chartData, setChartData] = useState<ChartData[]>([]);

  const [requestCompleted, setRequestCompleted] = useState(false);

  const fromDate = dateRange[0];
  const toDate = dateRange[1];

  const highlightTypesKey = highlightTypes.join(',');

  const orderKey = Object.entries(order)
    .sort(([first], [second]) => first.localeCompare(second))
    .map(([key, value]) => `${key}:${value}`)
    .join(',');

  useEffect(() => {
    addErrorRef.current = addError;
  }, [addError]);

  useEffect(() => {
    setIsLoadingRef.current = setIsLoading;
  }, [setIsLoading]);

  useEffect(() => {
    let isMounted = true;

    const requestId = ++requestIdRef.current;

    if (!fromDate || !toDate) {
      setChartData([]);
      setRequestCompleted(false);

      return () => {
        isMounted = false;
      };
    }

    const loadData = async () => {
      setIsLoadingRef.current(true);
      setRequestCompleted(false);

      try {
        const stableHighlightTypes = highlightTypesKey
          .split(',')
          .filter(Boolean)
          .map((value) => Number(value) as HighlightTypes);

        const stableOrder = parseOrderKey(orderKey);

        const responses = await Promise.all(stableHighlightTypes.map((highlightType) => getHighlightData(fromDate, toDate, partnership === 'All' ? '' : partnership, highlightType, clientFilterType)));

        if (!isMounted || requestId !== requestIdRef.current) {
          return;
        }

        const result = responses.map((response): ChartData => {
          const values: HighlightResponse = response && response.data ? response.data : {};

          const highlights = Array.isArray(values.highlights) ? values.highlights : [];

          return {
            id: values.type || values.tType || '',
            data: checkDataExist(highlights, fromDate, toDate),
          };
        });

        result.sort((first, second) => {
          const firstOrder = stableOrder[String(first.id)] ?? Number.MAX_SAFE_INTEGER;

          const secondOrder = stableOrder[String(second.id)] ?? Number.MAX_SAFE_INTEGER;

          return firstOrder - secondOrder;
        });

        setChartData(result);
      } catch (error: any) {
        if (!isMounted || requestId !== requestIdRef.current) {
          return;
        }

        setChartData([]);

        addErrorRef.current(JSON.stringify(error && error.response ? error.response : error));
      } finally {
        if (isMounted && requestId === requestIdRef.current) {
          setRequestCompleted(true);
          setIsLoadingRef.current(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [partnership, fromDate, toDate, clientFilterType, highlightTypesKey, orderKey]);

  const hasChartData = chartData.length > 0 && chartData.some((chart) => Array.isArray(chart.data) && chart.data.length > 0);

  return (
    <Grid container direction="column" alignContent="center" className={classes.content}>
      {hasChartData ? (
        <Grid container direction="row" className={classes.content} justify="space-between">
          <Grid item xs={12} className={classes.chartSection}>
            <LineChart data={chartData} currency={currency} colors={colors} valueType={valueType} />
          </Grid>

          <Grid item xs={12} className={classes.legend}>
            <span
              className={classes.dot}
              style={{
                backgroundColor: colors[0],
              }}
            />

            <span className={classes.legendTitle}>{legendFirstItem}</span>

            <span
              className={classes.secondDot}
              style={{
                backgroundColor: colors[1],
              }}
            />

            <span className={classes.legendTitle}>{legendSecondItem}</span>
          </Grid>
        </Grid>
      ) : (
        requestCompleted && (
          <Grid container direction="column" alignContent="center" alignItems="center" className={classes.emptyMessage}>
            <Grid item>{emptyMessage}</Grid>
          </Grid>
        )
      )}

      <Grid container className={classes.buttons}>
        <Grid item>
          {firstButtonName && firstButtonPath && (
            <Button startIcon={<DocumentIcon />} component={Link} to={firstButtonPath} className={classes.button}>
              {firstButtonName}
            </Button>
          )}
        </Grid>

        {secondButtonName && secondButtonPath && (
          <Grid item className={classes.secondButton}>
            <Button startIcon={<DocumentIcon />} className={classes.button} component={Link} to={secondButtonPath}>
              {secondButtonName}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
